import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ADD_CARD, formatPauseInstant, freePeriodNoticeLine, UPDATE_CARD_BUSY, UPDATE_CARD_FAILED } from "../lib/billingCopy";

/**
 * #1674 (D465, D468): the one-line notice on the player's home, drawn above whichever screen
 * `/dashboard` picks. It shows while the subscription is `past_due` (or `unpaid`, #1691) AND the webhook recorded that it
 * has no card (`has_payment_method === false`): a 100%-off promotion code whose free period has ended.
 * Access continues while Stripe retries (D196.4), up to the pause time the API names (D475); once the API says
 * access is paused (`grants_access: false`, or `unpaid`) the line says so (#1707). The line sends the payer to the same Stripe portal
 * Settings › Billing uses (`POST /billing/portal`). No payment form is built here.
 *
 * It reads `GET /billing/status` once. An admin or a coach never reads it. A paid player, a stopped
 * one, a player with no plan, an older API with no `has_payment_method`, and billing switched off
 * (503) all draw nothing. An 11-to-17 player is told a parent or guardian adds the card (D192.2).
 */
export default function FreePeriodEndedNotice() {
  const { user } = useAuth();
  const role = user?.role;
  const guardianPays = user?.age_band === "11_to_17";
  // No user yet, an admin and a coach never read it: the status route is the payer's.
  const skip = !user || role === "admin" || role === "coach";
  const [show, setShow] = useState(false);
  // #1707: the words follow the API's own answer about access, never the clock.
  const [paused, setPaused] = useState(false);
  const [pausesAt, setPausesAt] = useState(null);
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/billing/status");
        const data = res?.data;
        if (cancelled) return;
        setShow((data?.status === "past_due" || data?.status === "unpaid") && data?.has_payment_method === false);
        setPaused(data?.status === "unpaid" || data?.grants_access === false);
        setPausesAt(typeof data?.access_pauses_at === "string" ? data.access_pauses_at : null);
      } catch {
        // Billing off, or the read failed: the notice is a convenience, never a blocker.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skip]);

  if (skip || !show) return null;

  const openPortal = async () => {
    if (opening) return;
    setFailed(false);
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal", {});
      if (typeof data?.url === "string" && data.url.startsWith("https://")) {
        window.location.assign(data.url);
        return; // leaving the page: the button stays busy
      }
      throw new Error("no url");
    } catch {
      if (!mounted.current) return;
      setFailed(true);
      setOpening(false);
    }
  };

  return (
    <div
      data-testid="free-period-notice"
      role="status"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 8,
        padding: "10px 16px",
        background: "var(--nw-line-faint)",
        borderLeft: "3px solid var(--nw-alert)",
        fontSize: 13,
        lineHeight: 1.5,
        color: "var(--nw-ink)",
      }}
    >
      <Info size={16} color="#B42318" aria-hidden="true" style={{ flexShrink: 0 }} />
      <span style={{ flex: "1 1 200px", minWidth: 0 }}>{freePeriodNoticeLine({ paused, pausesText: formatPauseInstant(pausesAt), guardian: guardianPays })}</span>
      {!guardianPays && (
        <button
          type="button"
          data-testid="free-period-notice-add-card"
          onClick={openPortal}
          aria-disabled={opening || undefined}
          style={{
            minHeight: 44,
            padding: "0 14px",
            fontSize: 14,
            fontWeight: 700,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line-strong)",
            borderRadius: 10,
            color: "var(--nw-navy)",
            cursor: opening ? "not-allowed" : "pointer",
            opacity: opening ? 0.6 : 1,
          }}
        >
          {opening ? UPDATE_CARD_BUSY : ADD_CARD}
        </button>
      )}
      {failed && (
        <span data-testid="free-period-notice-error" style={{ flexBasis: "100%" }}>
          {UPDATE_CARD_FAILED}
        </span>
      )}
    </div>
  );
}
